import React, { useMemo } from 'react'
import { Highlight } from 'react-instantsearch-dom'

import { Stats, UserEatableSearchHit } from '../Search/searchHelpers'

import { AddJournalEatableRecordHandler } from './types'

import { JournalCardSearchEatable } from 'Components/JournalCardSearchEatable'
import { EatableType, Food, Recipe } from 'Models'
import { useInfiniteScrollSentinel } from 'Utils/helpers/useInfiniteScrollSentinel'
import { usePagination } from 'Utils/helpers/usePagination'

type MixedUserEatableHit = {
  eatableType: EatableType
  hit: UserEatableSearchHit<Food> | UserEatableSearchHit<Recipe>
}

type SearchHitsUserEatablesProps = {
  foodHits: UserEatableSearchHit<Food>[]
  recipeHits: UserEatableSearchHit<Recipe>[]
  initialDate: Date
  addJournalEatableRecord: AddJournalEatableRecordHandler
  onSaveSearchResult: () => void
}

export function SearchHitsUserEatables({
  foodHits,
  recipeHits,
  initialDate,
  addJournalEatableRecord,
  onSaveSearchResult
}: SearchHitsUserEatablesProps): JSX.Element {
  const hits = useMemo((): MixedUserEatableHit[] => {
    const foods: MixedUserEatableHit[] = foodHits.map((hit) => ({ eatableType: 'FOOD', hit }))
    const recipes: MixedUserEatableHit[] = recipeHits.map((hit) => ({ eatableType: 'RECIPE', hit }))

    return [...foods, ...recipes].sort((a, b) => a.hit.name.localeCompare(b.hit.name, 'de'))
  }, [foodHits, recipeHits])

  const { currentItems, loadMoreItems, currentPage, maxPage } = usePagination(hits, 50)

  const hasMore = currentPage < maxPage

  const { sentinelRef } = useInfiniteScrollSentinel({
    hasMore,
    onLoadMore: loadMoreItems,
    resetKey: currentItems.length
  })

  return (
    <>
      <div className="search-hits-count">
        <Stats
          nbHits={hits.length}
          hitName={{ singular: 'eigene Nahrungsmittel und Rezepte', plural: 'eigene Nahrungsmittel und Rezepte' }}
        />
      </div>

      {currentItems.map(({ eatableType, hit }) => (
        <JournalCardSearchEatable
          key={`${eatableType}-${hit.id}`}
          initialDate={initialDate}
          quantifiedEatable={{ [eatableType.toLowerCase()]: hit }}
          renderTitle={() => <Highlight attribute="name" tagName="mark" hit={hit} />}
          onSave={(eatable, date, mealCategory) => {
            addJournalEatableRecord(eatable, mealCategory, date, 'search')
            onSaveSearchResult()
          }}
        />
      ))}
      {hasMore && <div ref={sentinelRef} aria-hidden="true" />}
    </>
  )
}
